import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getRbacService } from '@src/container';
import type { UpdatePermissionData } from '@src/domain/rbac/value_objects/update_permission_data';

const PERMISSIONS_QUERY_KEY = 'permissions';

/**
 * Hook to get all permissions
 */
export const usePermissions = () => {
    const rbacService = getRbacService();

    return useQuery({
        queryKey: [PERMISSIONS_QUERY_KEY],
        queryFn: () => rbacService.listAllPermissions.execute(),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to get a permission by ID
 */
export const usePermission = (permissionId: string) => {
    const rbacService = getRbacService();

    return useQuery({
        queryKey: [PERMISSIONS_QUERY_KEY, permissionId],
        queryFn: () => rbacService.getPermissionById.execute(permissionId),
        enabled: !!permissionId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

/**
 * Hook to update a permission
 */
export const useUpdatePermission = () => {
    const rbacService = getRbacService();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ permissionId, data }: { permissionId: string; data: UpdatePermissionData }) =>
            rbacService.updatePermission.execute(permissionId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY] });
            queryClient.invalidateQueries({ queryKey: [PERMISSIONS_QUERY_KEY, variables.permissionId] });
        },
    });
};
